// EventDetailsDialog.tsx (no-flash fix version)
import React, { useCallback, useEffect, useMemo, useState, useLayoutEffect } from 'react';
import { DialogActions, DialogContent, DialogTitle, Typography, Button, Grid, List, ListItem, ListItemText} from '@mui/material';
import { useMembers } from '../../contexts/MemberContext';
import { IService } from '../../interfaces/Setting.interface';
import { AttendanceOptions, IAttendance, IBooking } from '../../interfaces/Booking.interface';
import { useSetting } from '../../contexts/SettingContext';

import { BookingHistory } from '../../components/BookingHistory';
import { useBookings } from '../../contexts/BookingContext';
import { SettingSection } from '../../components/Sections';
import { FormField } from '../../interfaces/User.interface';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FSelectField, FTextField } from '../../utils/FormBuilder/FormBuilder';
import FAutoComplete from '../../utils/FormBuilder/fields/FAutoComplete';
import { FDateInput } from '../../utils/FormBuilder/fields/FDateInput';
import { IMember } from '../../interfaces/Member.interface';
import { IUser } from '../../interfaces/User.interface';
import DialogWithClose from '../../components/DialogWithClose';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import { FNumberField } from '../../utils/FormBuilder/fields/FNumberField';
import { generateValidationSchema } from '../../utils/FormBuilder/Validators';

import { v4 as uuidv4 } from 'uuid';
import { Role, ServiceType } from '../../interfaces/constants';

import GoogleDriveUploader from '../../utils/googleDriveUploader';
import UnifiedFileViewer from '../../utils/UnifiedFileViewer';

import { useFetch } from '../../utils/FetchTemplate';

interface FormValues {
  service: IService;
  date: Date;
  duration: number;
  recurrenceType?: 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'none';
  recurrenceCount?: number;
  recurrenceId?: string;
  attendance: IAttendance;
  reminder: string;
  permanentReminder: string;
  feedback: string;
  participatingStudents?: IMember[];
}

interface EventDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (booking: IBooking, member?: IMember, closeDialog?: boolean) => void;
  onDelete: (booking: IBooking) => Promise<void>;
  onDeleteAll: (recurrenceId: string) => Promise<void>;
  onSave: (booking: IBooking, member?: IMember) => void;
  event: IBooking;
  mode: 'add' | 'show';
  disabled?: boolean;
  includeHistory?: boolean;
  user: IUser;
}

export const EventDetailsDialogNew: React.FC<EventDetailsDialogProps> = ({ open, onClose, onDelete, onDeleteAll, event, mode, onUpdate, onSave, includeHistory, user }) => {
  const { members } = useMembers();
  const { settings } = useSetting();
  const { bookings } = useBookings();
  const { postData, typedPostData } = useFetch();

  const [workingEvent, setWorkingEvent] = useState<IBooking>(event);

  const [bookingHistory, setBookingHistory] = useState<IBooking[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [participatingStudents, setParticipatingStudents] = useState<IMember[]>([]);

  const [vipError, setVipError] = useState<string | null>(null);

  const [reloadVideos, setReloadVideos] = useState(0);
  const [reloadFiles, setReloadFiles] = useState(0);

  useEffect(() => {
    if (event) setWorkingEvent(event);
  }, [event]);

  /*
  useEffect(() => {
    if (mode === 'show') {
      const found = bookings.find(b => b.bookingId === event.bookingId);
      if (found) setWorkingEvent(found);
    }
  }, [bookings, event.bookingId, mode]);
  */

  const sourceEvent = useMemo(() => {
    if (!event) return event;
    if (mode === 'show') {
      const found = bookings.find(b => b.bookingId === event.bookingId);
      return found ?? event;
    }
    return event;
  }, [mode, bookings, event]);

  // check if same as previous event
  const viewEvent: IBooking | undefined = useMemo(() => {
    if (workingEvent?.bookingId === sourceEvent?.bookingId) return workingEvent;
    return sourceEvent;
  }, [workingEvent, sourceEvent]);

  const handleUploaded = (type: 'video' | 'file') => {
    if (type === 'video') setReloadVideos(r => r + 1);
    else setReloadFiles(r => r + 1);
  };

  const handleConfirmDelete = useCallback(() => {
    if (!viewEvent) return;
    onDelete(viewEvent);
    setOpenDeleteDialog(false);
    onClose();
  }, [onClose, onDelete, viewEvent]);

  const dateField: FormField = {
    name: 'date',
    question: 'Date',
    validation: 'text',
    required: true,
    questionType: 'date',
    props: { InputLabelProps: { shrink: true } },
  };

  const recurrenceTypeField: FormField = {
    name: 'recurrenceType',
    question: 'Recurrence Type',
    validation: 'text',
    required: false,
    questionType: 'text',
    options: ['none', 'daily', 'weekly', 'biweekly', 'monthly'],
  };

  const recurrenceCountField: FormField = {
    name: 'recurrenceCount',
    question: 'Times ',
    validation: 'number',
    required: false,
    questionType: 'number',
    props: { variant: 'outlined', placeholder: 'e.g. 5' },
  };

  const durationField: FormField = {
    name: 'duration',
    question: 'Duration(mins) of this visit *',
    validation: 'number',
    required: true,
    questionType: 'number',
    props: { variant: 'outlined', placeholder: 'minutes' },
  };

  const reminderField: FormField = {
    name: 'reminder',
    question: 'Reminder for this visit only, will not show up in other visits',
    validation: 'text',
    required: false,
    questionType: 'text',
    props: { multiline: true, rows: 4, InputLabelProps: { shrink: true }, variant: 'outlined' },
  };

  const permanentReminderField: FormField = {
    name: 'permanentReminder',
    question: 'Permanent Reminder for this student, shows up in every visit (e.g.allergy, never needle)',
    validation: 'text',
    required: false,
    questionType: 'text',
    props: { multiline: true, rows: 5, InputLabelProps: { shrink: true }, variant: 'outlined' },
  };

  const attendanceField: FormField = {
    name: 'attendance',
    question: 'Attendance',
    validation: 'text',
    required: true,
    questionType: 'text',
    options: AttendanceOptions,
  };

  const serviceField: FormField = {
    name: 'service',
    question: 'Service',
    validation: 'object',
    required: true,
    questionType: 'autocomplete',
    data: settings.services,
    onRender: (item: any) => {
      const teacher = members.find(m => m.memberId === item.teacherId);
      const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unknown';
      return `${item.name} - ${item.type ?? ''} - ${teacherName}`;
    },
    props: {
      getOptionLabel: (item: any) => {
        const teacher = members.find(m => m.memberId === item.teacherId);
        const teacherName = teacher ? `${teacher.firstName} ${teacher.lastName}` : 'Unknown';
        return `${item.name} - ${item.type ?? ''} - ${teacherName}`;
      },
      isOptionEqualToValue: (option: any, value: any) => option.serviceId === value.serviceId,
    },
  };
  
  const feedbackField: FormField = {
    name: 'feedback',
    question: 'Your Feedback',
    validation: 'text',
    required: false,
    questionType: 'text',
    props: { multiline: true, rows: 4, InputLabelProps: { shrink: true }, variant: 'outlined' },
  };

  const defaultValues: FormValues = useMemo(() => {
    const ev = viewEvent;
    if (!ev) {
      return {
        service: {} as IService,
        date: new Date(),
        duration: 0,
        reminder: '',
        attendance: 'N/A' as IAttendance,
        permanentReminder: '',
        recurrenceType: 'none',
        recurrenceCount: 1,
        recurrenceId: 'none',
        feedback: '',
        participatingStudents: [],
      } as FormValues;
    }

    const defaultData = {
      service: settings.services.find(service => ev.serviceIds.includes(service.serviceId)),
      date: ev.startTime,
      duration: ev.duration,
      reminder: ev.description,
      attendance: ev.attendance,
      permanentReminder: ev.studentIds?.length > 0 ? members.find(member => ev.studentIds.includes(member.memberId))?.note : undefined,
      recurrenceType: ev.recurrenceType,
      recurrenceCount: ev.recurrenceCount,
      recurrenceId: ev.recurrenceId,
      feedback: ev.feedback,
      participatingStudents: [],
    } as FormValues;
    return defaultData;
  }, [viewEvent, members, settings.services]);

  const { control, handleSubmit, formState: { errors }, setValue, reset } = useForm<FormValues>({
    resolver: yupResolver(generateValidationSchema([serviceField, dateField, durationField])),
    defaultValues: defaultValues,
  });

  useLayoutEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  // Watchers
  const currentService = useWatch({ name: 'service', control });
  const isVip = currentService?.type === ServiceType.VIP;

  const recurrenceType = useWatch({ name: 'recurrenceType', control });
  const watchedDate = useWatch({ name: 'date', control });

  // Load participating students based on service type
  useEffect(() => {
    if (mode === 'show' && viewEvent) {
      if (viewEvent.studentIds?.length > 0) {
        const students = viewEvent.studentIds
          .map(id => members.find(m => m.memberId === id))
          .filter((m): m is IMember => !!m);
        setParticipatingStudents(students);
      } else {
        setParticipatingStudents([]);
      }
    } else if (mode === 'add' && currentService && currentService.serviceId) {
      if (currentService.type === ServiceType.Group && currentService.studentMemberIds) {
        const students = currentService.studentMemberIds
          .map(id => members.find(m => m.memberId === id))
          .filter((m): m is IMember => !!m);
        setParticipatingStudents(students);
      } else if (currentService.type === ServiceType.VIP) {
        if (participatingStudents.length === 0 && viewEvent && viewEvent.studentIds?.length > 0) {
          const student = members.find(m => viewEvent.studentIds.includes(m.memberId));
          if (student) setParticipatingStudents([student]);
        }
      } else {
        setParticipatingStudents([]);
      }
    }
  }, [mode, currentService, members, viewEvent]);

  //set count = 1 for no recurrence booking
  useEffect(() => {
    if (recurrenceType === 'none') setValue('recurrenceCount', 1);
  }, [recurrenceType, setValue]);

  const filterBookings = useCallback((member: IMember) => {
    return bookings.filter(b => b.studentIds?.includes(member.memberId));
  }, [bookings]);

  useEffect(() => {
    if (participatingStudents.length === 1 && includeHistory) {
      setBookingHistory(filterBookings(participatingStudents[0]));
    }
    if (participatingStudents.length === 1) {
      setValue('permanentReminder', participatingStudents[0].note || '');
    }
  }, [participatingStudents, filterBookings, includeHistory, setValue]);

  const validateVipBeforeCreate = async (serviceId: string, memberId: string, hoursNeeded: number) => {
    try {
      const result = await typedPostData<{ memberId: string; serviceId: string }, { pricePerHour: number; hoursRemaining: number }>(
        '/api/payment/getVipBalance',
        { memberId, serviceId }
      );
      if (result.hoursRemaining < hoursNeeded) {
        setVipError('Insufficient VIP hours for this service');
        return false;
      }
      setVipError(null);
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  const onSubmitWrapper = async (data: FormValues) => {
    const duration = data.duration;
    const recurrenceType = data.recurrenceType ?? 'none';
    const recurrenceCount = data.recurrenceCount ?? 1;

    const recurrenceDays = {
      daily: 1,
      weekly: 7,
      biweekly: 14,
      monthly: 30,
      none: 0,
    }[recurrenceType];

    const list: IBooking[] = [];
    const recurrenceId = uuidv4();

    if (data.service.type === ServiceType.VIP) {
      const studentToUse = participatingStudents[0];
      const hoursNeeded = duration / 60;
      const ok = await validateVipBeforeCreate(data.service.serviceId, studentToUse?.memberId || '', hoursNeeded);
      if (!ok) return;
    }

    if (data.service.type === ServiceType.Group || (data.service.type === ServiceType.VIP && participatingStudents.length > 0)) {
      const studentToUse = participatingStudents[0];

      for (let i = 0; i < recurrenceCount; i++) {
        const offsetDays = recurrenceDays * i;
        const currentStart = new Date(data.date);
        currentStart.setDate(currentStart.getDate() + offsetDays);
        const currentEnd = new Date(currentStart.getTime() + duration * 60000);

        const booking: IBooking = {
          ...(viewEvent as IBooking),
          serviceIds: [data.service.serviceId],
          serviceNames: [data.service.name],
          studentIds: data.service.type === ServiceType.Group
            ? participatingStudents.map(s => s.memberId)
            : studentToUse ? [studentToUse.memberId] : [],
          teacherId: data.service.teacherId,
          startTime: currentStart,
          endTime: currentEnd,
          duration: data.duration,
          title: data.service.type === ServiceType.Group
            ? `${data.service.name} - Group Class`
            : `${studentToUse?.firstName}'s appointment for ${data.service.name} - ${data.service.type}`,
          description: data.reminder,
          attendance: data.attendance,
          status: 'pending',
          recurrenceType: data.recurrenceType ?? 'none',
          recurrenceCount: data.recurrenceCount ?? 1,
          recurrenceId: recurrenceId,
        };

        list.push(booking);
      }

      const member: IMember | undefined = participatingStudents.length === 1
        ? { ...participatingStudents[0], note: data.permanentReminder }
        : undefined;

      if (mode === 'add') {
        list.forEach(b => onSave(b, member));
        onClose();
      }
      if (mode === 'show') {
        const currentStart = new Date(data.date);
        const currentEnd = new Date(currentStart.getTime() + data.duration * 60000);

        const updatedBooking: IBooking = {
          ...(viewEvent as IBooking),
          serviceIds: [data.service.serviceId],
          serviceNames: [data.service.name],
          studentIds: data.service.type === ServiceType.Group
            ? participatingStudents.map(s => s.memberId)
            : studentToUse ? [studentToUse.memberId] : (viewEvent?.studentIds || []),
          teacherId: data.service.teacherId,
          startTime: currentStart,
          endTime: currentEnd,
          duration: data.duration,
          title: data.service.type === ServiceType.Group
            ? `${data.service.name} - Group Class`
            : `${studentToUse?.firstName || (viewEvent?.studentIds?.length ? members.find(m => viewEvent!.studentIds!.includes(m.memberId))?.firstName : '')}'s appointment for ${data.service.name} - ${data.service.type}`,
          description: data.reminder,
          attendance: data.attendance,
          recurrenceType: data.recurrenceType,
          recurrenceCount: data.recurrenceCount,
        };

        onUpdate(updatedBooking, member);
        onClose();
      }
    }
  };

  return (
    <div>
      <DialogWithClose key={viewEvent?.bookingId || mode} open={open} onClose={onClose} fullWidth maxWidth="lg">
        <DialogTitle>
          <Typography>Event Details</Typography>
        </DialogTitle>
        <DialogContent style={{ height: '80vh', overflow: 'hidden' }}>
          <Grid container spacing={2} style={{ height: '100%' }}>
            {/* Left Column */}
            <Grid item xs={6} style={{ overflowY: 'auto', height: '100%', paddingRight: '16px' }}>
              <form key={viewEvent?.bookingId || mode}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FAutoComplete
                      field={{
                        ...serviceField,
                        props: {
                          ...serviceField.props,
                          disabled: user?.role !== Role.Admin,
                        },
                      }}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FDateInput
                      field={{
                        ...dateField,
                        props: {
                          ...dateField.props,
                          disabled: user?.role !== Role.Admin,
                        },
                      }}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <FSelectField
                          field={{
                            ...recurrenceTypeField,
                            props: {
                              ...recurrenceTypeField.props,
                              disabled: mode === 'show',
                            },
                          }}
                          control={control}
                          errors={errors}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FNumberField
                          field={{
                            ...recurrenceCountField,
                            props: {
                              ...recurrenceCountField.props,
                              disabled: recurrenceType === 'none' || mode === 'show',
                            },
                          }}
                          control={control}
                          errors={errors}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <FNumberField
                      field={{
                        ...durationField,
                        props: {
                          ...durationField.props,
                          disabled: user?.role !== Role.Admin,
                        },
                      }}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                  {vipError && (
                    <Grid item xs={12}>
                      <Typography color="error">{vipError}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FTextField
                      field={{
                        ...reminderField,
                        props: {
                          ...reminderField.props,
                          disabled: user?.role !== Role.Admin,
                        },
                      }}
                      control={control}
                      errors={errors}
                    />
                  </Grid>

                  {/* Student List Section */}
                  {(user?.role === Role.Admin || user?.role === Role.Teacher) && (
                    <Grid item xs={12}>
                      <Typography variant="h6" gutterBottom>
                        Participating Students
                      </Typography>
                      {mode === 'show' ? (
                        <div>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Students in this event
                          </Typography>
                          {participatingStudents.length > 0 ? (
                            <List>
                              {participatingStudents.map(student => (
                                <ListItem key={student.memberId}>
                                  <ListItemText primary={`${student.firstName} ${student.lastName}`} secondary={student.email} />
                                </ListItem>
                              ))}
                            </List>
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No students assigned to this event
                            </Typography>
                          )}
                        </div>
                      ) : !currentService || !currentService.serviceId ? (
                        <Typography variant="body2" color="textSecondary">
                          Please select a service first
                        </Typography>
                      ) : currentService.type === ServiceType.Group ? (
                        <div>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Students are managed in the service settings
                          </Typography>
                          <List>
                            {participatingStudents.map(student => (
                              <ListItem key={student.memberId}>
                                <ListItemText primary={`${student.firstName} ${student.lastName}`} secondary={student.email} />
                              </ListItem>
                            ))}
                          </List>
                        </div>
                      ) : (
                        <div>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Select a student for this 1-on-1 service
                          </Typography>
                          <FAutoComplete
                            field={{
                              name: 'participatingStudent',
                              question: 'Select Student',
                              validation: 'object',
                              required: false,
                              questionType: 'autocomplete',
                              data: members.filter(m => m.role === Role.Student),
                              props: {
                                getOptionLabel: (option: any) => (option ? `${option.firstName} ${option.lastName} / ${option.email}` : ''),
                                isOptionEqualToValue: (option: any, value: any) => option?.memberId === value?.memberId,
                                value: participatingStudents[0] || null,
                                onChange: (_event: any, newValue: IMember | null) => {
                                  if (newValue) setParticipatingStudents([newValue]);
                                  else setParticipatingStudents([]);
                                },
                              },
                            }}
                            control={control}
                            errors={errors}
                          />
                        </div>
                      )}
                    </Grid>
                  )}
                </Grid>
              </form>
            </Grid>

            {/* Right Column */}
            <Grid item xs={6} style={{ overflowY: 'auto', height: '100%', paddingLeft: '16px' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FTextField
                    field={{
                      ...permanentReminderField,
                      props: {
                        ...permanentReminderField.props,
                        disabled: user?.role !== Role.Admin,
                      },
                    }}
                    control={control}
                    errors={errors}
                  />
                </Grid>
                {/* {mode === 'show' && (
                  <Grid item xs={12}>
                    <FSelectField
                      field={{
                        ...attendanceField,
                        props: {
                          ...attendanceField.props,
                          disabled: user?.role !== Role.Admin,
                        },
                      }}
                      control={control}
                      errors={errors}
                    />
                  </Grid>
                )} */}
                {user?.role === Role.Admin &&
                  mode === 'show' &&
                  currentService?.type === ServiceType.VIP &&
                  participatingStudents.length === 1 &&
                  viewEvent?.endTime &&
                  new Date() > new Date(viewEvent.endTime) &&
                  !viewEvent.confirmed && (
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="success"
                        onClick={async () => {
                          try {
                            await postData('/api/booking/confirmVipEvent', { bookingId: viewEvent.bookingId });
                            setWorkingEvent(prev => ({ ...(prev ?? viewEvent), confirmed: true, status: 'finished' } as any));
                          } catch (err) {
                            console.error('Confirm Event failed', err);
                          }
                        }}
                      >
                        Confirm Event
                      </Button>
                    </Grid>
                  )}
              </Grid>

              {includeHistory && (
                <SettingSection title="Visit History">
                  <BookingHistory data={bookingHistory} />
                </SettingSection>
              )}

              {/* 只有admin和teacher可以上传 */}
              {mode === 'show' && (user?.role === Role.Teacher || user?.role === Role.Admin) && (
                <Grid item xs={12}>
                  <GoogleDriveUploader
                    serviceName={currentService?.name}
                    calendarDate={watchedDate}
                    bookingId={viewEvent?.bookingId}
                    folderId={viewEvent?.folderId}
                    onUploaded={handleUploaded}
                  />
                </Grid>
              )}

              {mode === 'show' && !!viewEvent?.bookingId && (
                <SettingSection title="Files & Videos">
                  <UnifiedFileViewer bookingId={viewEvent.bookingId} reload={reloadFiles} userRole={user?.role} />
                </SettingSection>
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            {/* Move the delete button to the most left of the panel */}
            {mode === 'show' && (
              <div>
                <ConfirmDeleteDialog
                  open={openDeleteDialog}
                  onClose={() => setOpenDeleteDialog(false)}
                  onConfirm={handleConfirmDelete}
                  itemName="event"
                />
                {user?.role === Role.Admin && (
                  <Button color="error" onClick={async () => { setOpenDeleteDialog(true); }}>
                    Delete
                  </Button>
                )}
                <ConfirmDeleteDialog
                  open={openDeleteAllDialog}
                  onClose={() => setOpenDeleteAllDialog(false)}
                  onConfirm={async () => {
                    if (viewEvent?.recurrenceId) {
                      await onDeleteAll?.(viewEvent.recurrenceId);
                    }
                    setOpenDeleteAllDialog(false);
                    onClose();
                  }}
                  itemName="all related events"
                />
                {user?.role === Role.Admin && (
                  <Button color="error" onClick={() => setOpenDeleteAllDialog(true)}>
                    Delete All
                  </Button>
                )}
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center' }} />

            {user?.role === Role.Admin && (
              <Button variant="contained" color="primary" onClick={handleSubmit(onSubmitWrapper)} style={{ fontSize: '1.25rem' }}>
                Save
              </Button>
            )}
          </div>
        </DialogActions>
      </DialogWithClose>
    </div>
  );
};
